import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { Database } from '../types/database'
import type { MyAccess } from '../types/school'

type Subscription = Database['public']['Tables']['subscriptions']['Row']

// Fase do ciclo de vida para EXIBIÇÃO (mensagens/cards). Não usar para gating
// de acesso — para isso existem isPremium/isEscolar/getUsageLimits/canAccess,
// que tratam o trial como acesso completo.
export type SubscriptionPhase = 'escolar' | 'premium' | 'trialing' | 'trial_expired' | 'none'

interface SubscriptionContextType {
  subscription: Subscription | null
  loading: boolean
  isActive: boolean
  isTrialing: boolean
  isTrialExpired: boolean
  isPremium: boolean
  isEscolar: boolean
  needsSubscription: boolean
  hadExpiredTrial: boolean
  subscriptionPhase: SubscriptionPhase
  expiredTrialEndDate: Date | null
  trialEndDate: Date | null
  daysLeftInTrial: number | null
  canAccess: (feature: string) => boolean
  refreshSubscription: () => Promise<void>
  getUsageLimits: () => UsageLimits
  /**
   * Preenchido só quando o plano efetivo vem do vínculo institucional
   * (`get_my_access().source === 'school'`). `null` para todo mundo que não é
   * membro de escola — hoje, a totalidade das contas em produção.
   */
  schoolAccess: SchoolAccessInfo | null
}

/** Origem institucional do plano, para o card de plano da navegação. */
export interface SchoolAccessInfo {
  name: string
  status: string
  /** Prazo da carência quando `status = 'past_due'`. */
  graceUntil: string | null
  pilotUntil: string | null
}

interface UsageLimits {
  questionsPerMonth: number
  assessmentsPerMonth: number
  templatesAccess: boolean
  aiGeneration: boolean
  collaboration: boolean
  analytics: boolean
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

/** Origem do plano quando ele não vem de uma linha ativa em `subscriptions`. */
interface EffectiveAccess {
  plan: string
  /** `get_my_access().source`: 'subscription' | 'school' | 'profile' | 'none'. */
  source: string
  /** Só quando `source === 'school'`; o caminho legado nunca preenche. */
  school: SchoolAccessInfo | null
}

/**
 * Plano efetivo quando não existe assinatura ativa na tabela `subscriptions`
 * (que é o caso da maioria absoluta das contas em produção).
 *
 * A autoridade é a RPC `get_my_access()`, que resolve a precedência no banco:
 * assinatura própria > escola com acesso (`school_has_access`) > `profiles.plano`.
 * Consultar `subscriptions` de novo aqui seria errado por construção, e ler só
 * `profiles.plano` deixaria membro de escola sem o plano institucional.
 *
 * Se a RPC não responder (ambiente sem a migration, sem rede, permissão), cai
 * no caminho legado `profiles.plano` — exatamente o comportamento anterior.
 * Escola em piloto vencido/suspensa não devolve plano da escola: a RPC devolve
 * o plano próprio do usuário e o app segue normal.
 */
const fetchEffectiveAccess = async (userId: string): Promise<EffectiveAccess | null> => {
  try {
    const { data, error } = await supabase.rpc('get_my_access')
    if (error) throw error
    // RETURNS TABLE(...) chega como array de uma linha pelo supabase-js.
    const row = (Array.isArray(data) ? data[0] : data) as MyAccess | null | undefined
    if (row?.plan) {
      // `school_name` só vem quando a escola concede o plano (`school_has_access`
      // verdadeiro). Escola em piloto vencido ou suspensa cai em `source` próprio,
      // e aí não há origem institucional a exibir.
      const school: SchoolAccessInfo | null =
        row.source === 'school' && row.school_name
          ? {
              name: row.school_name,
              status: row.school_status ?? 'active',
              graceUntil: row.grace_until ?? null,
              pilotUntil: row.pilot_until ?? null
            }
          : null
      return { plan: row.plan, source: row.source || 'rpc', school }
    }
  } catch {
    // RPC indisponível: segue para o caminho legado abaixo.
  }

  const { data: profileData } = await supabase
    .from('profiles')
    .select('plano')
    .eq('id', userId)
    .single()

  return profileData?.plano
    ? { plan: profileData.plano, source: 'profile_fallback', school: null }
    : null
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  // Para qual usuário a última busca TERMINOU (null = visita anônima;
  // undefined = nenhuma ainda). Enquanto não for o usuário atual, o contexto
  // se declara carregando — ver `loading` no value do Provider.
  const [loadedForUserId, setLoadedForUserId] = useState<string | null | undefined>(undefined)
  const [schoolAccess, setSchoolAccess] = useState<SchoolAccessInfo | null>(null)

  const fetchSubscription = async () => {
    const fetchingFor = user?.id ?? null

    if (!user) {
      setSubscription(null)
      setAllSubscriptions([])
      setSchoolAccess(null)
      setLoading(false)
      setInitialized(true)
      setLoadedForUserId(null)
      return
    }

    try {
      // Single query to get all subscriptions - filter active/trialing in memory
      const { data: allSubs, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        setSubscription(null)
        setAllSubscriptions([])
        setSchoolAccess(null)
      } else {
        setAllSubscriptions(allSubs || [])

        // Find active/trialing subscription in memory
        const activeSub = allSubs?.find(sub =>
          sub.status === 'active' || sub.status === 'trialing'
        ) || null

        if (activeSub) {
          setSubscription(activeSub)
          // Assinatura própria ativa: o plano não vem da escola, então não há
          // origem institucional a anunciar mesmo que o usuário seja membro.
          setSchoolAccess(null)
        } else {
          setSubscription(null)

          // Fallback: sem linha ativa em `subscriptions`, o plano efetivo vem
          // do banco (ver fetchEffectiveAccess).
          const access = await fetchEffectiveAccess(user.id)
          setSchoolAccess(access?.school ?? null)

          if (access && ['premium', 'escolar'].includes(access.plan)) {
            const virtualSubscription = {
              id: 'virtual-' + user.id,
              user_id: user.id,
              stripe_customer_id: null,
              stripe_subscription_id: null,
              plano: access.plan,
              status: 'active',
              current_period_start: null,
              current_period_end: null,
              cancel_at_period_end: false,
              metadata: { source: access.source },
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              plan_id: null,
              trial_start: null,
              trial_end: null,
              trial_status: null
            }
            setSubscription(virtualSubscription)
          }
        }
      }
    } catch (error) {
      setSubscription(null)
      setAllSubscriptions([])
      setSchoolAccess(null)
    } finally {
      setLoading(false)
      setInitialized(true)
      setLoadedForUserId(fetchingFor)
    }
  }

  useEffect(() => {
    // Only fetch subscription when auth is not loading and we have a user state (even if null)
    if (!authLoading && !initialized) {
      fetchSubscription()
    }
  }, [user, authLoading, initialized])

  // Track whether the initial user ID has been set to avoid resetting
  // on first auth resolution (null -> userId), which causes a double-fetch race condition
  const prevUserIdRef = useRef<string | null | undefined>(undefined)

  // Reset when user genuinely changes (e.g., logout then login as different user)
  // but NOT on the initial auth resolution
  useEffect(() => {
    if (!authLoading) {
      const isInitialSet = prevUserIdRef.current === undefined
      const userActuallyChanged = prevUserIdRef.current !== undefined && prevUserIdRef.current !== (user?.id ?? null)
      prevUserIdRef.current = user?.id ?? null

      if (!isInitialSet && userActuallyChanged) {
        setInitialized(false)
        setLoading(true)
      }
    }
  }, [user?.id, authLoading])

  // Refresh when AuthContext finishes auto-activating a trial after signup.
  // `setLoading(true)` antes da busca: o AuthContext desliga `trialActivating`
  // logo depois de disparar o evento, e sem isto haveria um render com
  // loading=false e a assinatura ANTIGA (nenhuma) — o TrialGuard redirecionaria
  // para /app/billing justamente quem acabou de ganhar o trial.
  useEffect(() => {
    const handler = () => {
      setLoading(true)
      void fetchSubscription()
    }
    window.addEventListener('trial-activated', handler)
    return () => window.removeEventListener('trial-activated', handler)
  }, [user?.id])

  // Calculate subscription status
  const isActive = subscription?.status === 'active'

  // Check if trial has expired by comparing trial_end date with current date
  const trialEndDate = subscription?.trial_end ? new Date(subscription.trial_end) : null
  const isTrialExpired = subscription?.status === 'trialing' && trialEndDate ? trialEndDate.getTime() < Date.now() : false

  // isTrialing should only be true if status is 'trialing' AND trial hasn't expired
  const isTrialing = subscription?.status === 'trialing' && !isTrialExpired

  // For debugging: also check if user has any premium/escolar subscription regardless of status
  const hasAnyPremiumSubscription = allSubscriptions?.some(sub =>
    sub.plano === 'premium' && ['active', 'trialing'].includes(sub.status)
  ) || false

  const hasAnyEscolarSubscription = allSubscriptions?.some(sub =>
    sub.plano === 'escolar' && ['active', 'trialing'].includes(sub.status)
  ) || false



  // Use more inclusive logic for premium detection
  const isPremium = hasAnyPremiumSubscription || ((isActive || isTrialing) && subscription?.plano === 'premium')
  const isEscolar = hasAnyEscolarSubscription || ((isActive || isTrialing) && subscription?.plano === 'escolar')

  // Check if user needs to subscribe (no active subscription or trial)
  const needsSubscription = !isActive && !isTrialing

  // Detect an expired trial even after the cron flips status to 'canceled'.
  // `subscription` only holds active/trialing rows, so post-cron it is null —
  // the trial history lives in allSubscriptions (ordered created_at desc).
  const latestTrialSub = allSubscriptions.find(sub => sub.trial_end)
  const expiredTrialEndDate = latestTrialSub?.trial_end ? new Date(latestTrialSub.trial_end) : null
  // Um trial cancelado manualmente (cancel-trial) fica com trial_end no futuro,
  // então a comparação de datas sozinha não o detecta — o trial_status detecta.
  const hadEndedTrialStatus = allSubscriptions.some(sub =>
    sub.trial_status === 'expired' || sub.trial_status === 'cancelled'
  )
  const hadExpiredTrial = !isActive && !isTrialing && (
    (!!expiredTrialEndDate && expiredTrialEndDate.getTime() < Date.now()) ||
    hadEndedTrialStatus
  )

  // Calculate trial information
  const daysLeftInTrial = trialEndDate && isTrialing
    ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null

  // Fase única do ciclo de vida, com precedência correta:
  // pago ativo > trial ativo > trial expirado (pré-cron, pós-cron ou cancelado) > nada.
  // isPremium é true durante o trial (acesso), por isso os cards de exibição
  // devem usar esta fase — nunca isPremium — para decidir a mensagem.
  const subscriptionPhase: SubscriptionPhase =
    isActive && subscription?.plano === 'escolar' ? 'escolar'
    : isActive && subscription?.plano === 'premium' ? 'premium'
    : isTrialing ? 'trialing'
    : (isTrialExpired || hadExpiredTrial) ? 'trial_expired'
    : 'none'

  const getUsageLimits = (): UsageLimits => {
    if (isEscolar) {
      return {
        questionsPerMonth: -1, // unlimited
        assessmentsPerMonth: -1, // unlimited
        templatesAccess: true,
        aiGeneration: true,
        collaboration: true,
        analytics: true
      }
    }

    if (isPremium || isTrialing) {
      return {
        questionsPerMonth: -1, // unlimited
        assessmentsPerMonth: -1, // unlimited
        templatesAccess: true,
        aiGeneration: true,
        collaboration: true,
        analytics: true
      }
    }

    // No active subscription or trial - no access
    return {
      questionsPerMonth: 0,
      assessmentsPerMonth: 0,
      templatesAccess: false,
      aiGeneration: false,
      collaboration: false,
      analytics: false
    }
  }

  // UX-only gate: controla visibilidade na UI, não é barreira de segurança.
  // Enforcement real: TrialGuard nas rotas /app/* e policies RLS
  // (can_create_assessment / can_download_pdf) no banco.
  const canAccess = (feature: string): boolean => {
    const limits = getUsageLimits()
    
    const features = {
      'unlimited_questions': limits.questionsPerMonth === -1,
      'unlimited_assessments': limits.assessmentsPerMonth === -1,
      'premium_templates': limits.templatesAccess,
      'ai_generation': limits.aiGeneration,
      'collaboration': limits.collaboration,
      'analytics': limits.analytics
    }

    return features[feature as keyof typeof features] || false
  }

  // Corrida do login: na tela de login a busca anônima já terminou
  // (loading=false, sem assinatura). Quando `user` muda, o LoginForm navega
  // para /app no mesmo render, e o efeito que reinicia a busca só roda depois
  // do commit — o TrialGuard lia loading=false + sem assinatura e mandava um
  // Premium para /app/billing. Enquanto a última busca concluída não for a
  // deste usuário, estamos carregando (derivado, sem depender de efeito).
  const staleForCurrentUser = loadedForUserId !== (user?.id ?? null)

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      loading: loading || authLoading || staleForCurrentUser,
      isActive,
      isTrialing,
      isTrialExpired,
      isPremium,
      isEscolar,
      needsSubscription,
      hadExpiredTrial,
      subscriptionPhase,
      expiredTrialEndDate,
      trialEndDate,
      daysLeftInTrial,
      canAccess,
      refreshSubscription: fetchSubscription,
      getUsageLimits,
      schoolAccess
    }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}